// istanbul ignore file — composition shell; per-pane logic lives in `section_content_registry.js` and the page modules.

import { I18n } from '@calendly/i18n'
import { IconX } from '@calendly/ui/components/glyphs/IconX'
import { IconButton } from '@calendly/ui/components/icon-button'
import { PageHeader } from '@calendly/ui/components/page-header'
import { Tab, Tabs } from '@calendly/ui/components/tabs'
import { createMemoryHistory } from 'history'
import { useMemo } from 'react'
import { Link as RouterLink, Route } from 'react-router-dom'

import {
  navigationAnalyticsIdForSettingsTab,
  trackSettingsTabNavigation,
  useTrack,
} from 'hooks/analytics_events'
import { toSentenceCase } from 'shared/utils/to_sentence_case'

import {
  SETTINGS_MODAL_PANE_KIND,
  SETTINGS_SECTION,
  mergeSettingsSearch,
} from './constants'
import { SettingsModalContentColumn, SettingsModalHeaderActionsHost } from './layout_context'
import { getSettingsSectionTitle } from './nav_definitions'
import {
  renderSettingsModalPaneBody,
  resolveSettingsModalPaneDescriptor,
} from './section_content_registry'
import {
  getSettingsModalNotetakerTabs,
  normalizeSettingsModalAiTab,
  useShowSettingsModalAdminNotetakerAccess,
  useShowSettingsModalNotetakerTab,
} from './settings_modal_ai_page'
import { SettingsModalManagedEventTypesHeader } from './settings_modal_managed_event_types_page'
import {
  SettingsModalMemoryRouteShell,
  confirmMemoryRouteNavigation,
} from './settings_modal_memory_route_shell'
import {
  getValidTabsForSection,
  SETTINGS_MEMORY_ROUTE_TAB_CONFIG,
} from './settings_modal_memory_tabs'
import { SettingsModalOrgDirectoryHeader } from './settings_modal_org_directory_page'
import { SettingsModalPeopleHeader } from './settings_modal_people_page'
import {
  AdminAccessSettingsModalTabbedHeader,
  AdminSecuritySettingsModalTabbedHeader,
  PersonalSecuritySettingsModalTabbedHeader,
  ProfileSettingsModalTabbedHeader,
} from './settings_modal_tabbed_headers'
import { ADMIN_GROUPS_BASE } from './settings_modal_user_groups_constants'
import { SETTINGS_MODAL_CALENDAR_SUBNAV } from './settingsConfig'
import { confirmDiscardIfDirty } from './use_settings_modal_url_normalization'

/**
 * Memory routes that draw {@link PageHeaderWithTabs} in the modal column header (desktop + mobile detail).
 *
 * `adminPermissions` is intentionally absent: that section has no row in `SETTINGS_MODAL_ADMIN_NAV`,
 * is not a `KNOWN_SETTINGS_SECTIONS` entry, and the legacy alias is always rewritten to
 * `SETTINGS_SECTION.adminPeople` before the body renders.
 */
const MEMORY_ROUTE_TABBED_HEADER_BY_SECTION = {
  [SETTINGS_SECTION.profile]: ProfileSettingsModalTabbedHeader,
  [SETTINGS_SECTION.security]: PersonalSecuritySettingsModalTabbedHeader,
  [SETTINGS_SECTION.adminSecurity]: AdminSecuritySettingsModalTabbedHeader,
  [SETTINGS_SECTION.adminAccess]: AdminAccessSettingsModalTabbedHeader,
}

function translateCalendarSubnavLabel(slug) {
  switch (slug) {
    case 'calendar':
      return I18n.t('publisher.personal_settings.calendar_section.tabs.calendar')
    case 'availability':
      return I18n.t('publisher.personal_settings.calendar_section.tabs.availability')
    case 'advanced':
      return I18n.t('publisher.personal_settings.calendar_section.tabs.advanced')
    default:
      return I18n.t('publisher.personal_settings.calendar_section.tabs.calendar')
  }
}

function translateNotetakerSubnavLabel(slug) {
  switch (slug) {
    case 'notetaker':
      return I18n.t('publisher.personal_settings.navigation.notetaker_settings_tab')
    case 'notetaker_access':
      return I18n.t('publisher.admin.navigation.notetaker_access')
    default:
      return I18n.t('publisher.personal_settings.navigation.notetaker_settings_tab')
  }
}

/**
 * Drop slugs that aren't in the section's tab config (or that are flag-disabled today) so the
 * memory router opens at the section's default tab instead of a 404-ish empty pane. `null` when
 * the section has no memory-tab config (e.g. `adminBilling`) — pass `settingsTab` through
 * unchanged in that case so unrelated query params survive.
 */
function sanitizeSettingsTab(effectiveSec, settingsTab, ctx) {
  if (settingsTab == null) {
    return settingsTab
  }
  const validTabs = getValidTabsForSection(effectiveSec, ctx)
  if (validTabs == null) {
    return settingsTab
  }
  return validTabs.includes(settingsTab) ? settingsTab : null
}

/**
 * Renders the main pane of the settings modal — either a column page, a tabbed memory-router
 * shell, or the Profile fallback. Pulled out of `<SettingsModal>` as a presentational sub-component
 * so the host stays a thin composition shell.
 *
 * @param {object} props
 * @param {string} props.effectiveSec — section to render (`SETTINGS_SECTION` value)
 * @param {'desktop' | 'mobile'} [props.variant]
 * @param {string} [props.dataTestId] — overrides the auto-derived `settings-modal-body-*` testid
 * @param {ReturnType<typeof import('./nav_definitions').buildSettingsModalAccountNavItems>} props.accountNavItems
 * @param {ReturnType<typeof import('./nav_definitions').buildSettingsModalAdminNavItems>} props.adminNavItems
 * @param {Set<string>} props.adminEnabledSections
 * @param {string | null} props.settingsTab
 * @param {boolean} props.joinAnOrgForAccessNav — `obi_home_new_join_an_org` variant
 * @param {boolean} props.organizationIntegrationsTabEnabled — integrations tab enabled for admin access (flag + policy check)
 * @param {boolean} props.bookingVerificationEnabled — `trust_and_safety_email_verification_feature`
 * @param {boolean} props.reactiveBlockEnabled — `trust_and_safety_reactive_block`
 * @param {string} props.personalSecurityMemoryInitialPath — flag-driven first path for personal Security
 * @param {string} props.accessRegistryInitialPath — flag-driven first path for admin Access
 * @param {import('react-router').History} props.history — real browser history (tab URL sync)
 * @param {import('react-router').Location} props.location — real browser location
 * @param {() => void} props.closeModal
 */
export const SettingsModalBody = ({
  effectiveSec,
  variant = 'desktop',
  dataTestId,
  accountNavItems,
  adminNavItems,
  adminEnabledSections,
  settingsTab,
  joinAnOrgForAccessNav,
  organizationIntegrationsTabEnabled,
  bookingVerificationEnabled,
  reactiveBlockEnabled,
  personalSecurityMemoryInitialPath,
  accessRegistryInitialPath,
  history,
  location,
  closeModal,
}) => {
  const track = useTrack()
  const sourceUrl = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`
  const pageTitle = getSettingsSectionTitle(effectiveSec, accountNavItems, adminNavItems)
  const showCloseInPageHeader = variant === 'desktop'
  const userGroupsMemoryHistory = useMemo(() => createMemoryHistory({
    initialEntries: [ADMIN_GROUPS_BASE],
    initialIndex: 0,
    getUserConfirmation: confirmMemoryRouteNavigation,
  }), [])

  const calendarTabCfg = SETTINGS_MEMORY_ROUTE_TAB_CONFIG[SETTINGS_SECTION.calendar]
  const calendarMemoryDefaultSlug = calendarTabCfg?.defaultTab ?? 'calendar'
  const calendarMemoryActiveSlug = (
    settingsTab && calendarTabCfg?.paths?.[settingsTab]
  ) ? settingsTab : calendarMemoryDefaultSlug
  const showAdminNotetakerAccessTab = useShowSettingsModalAdminNotetakerAccess()
  const showNotetakerTab = useShowSettingsModalNotetakerTab()
  const notetakerTabCtx = { showAdminNotetakerAccessTab, showNotetakerTab }
  const notetakerActiveSlug = normalizeSettingsModalAiTab(settingsTab, notetakerTabCtx)

  const pageHeaderTabs = (() => {
    if (effectiveSec === SETTINGS_SECTION.calendar) {
      return (
        <Tabs underlined aria-label={pageTitle}>
          {SETTINGS_MODAL_CALENDAR_SUBNAV.map(({ slug }) => (
            <Tab
              key={slug}
              as={RouterLink}
              replace
              to={{
                pathname: location.pathname,
                search: mergeSettingsSearch(SETTINGS_SECTION.calendar, location.search, slug),
                hash: location.hash ?? '',
              }}
              onClick={(event) => {
                if (!confirmDiscardIfDirty()) {
                  event.preventDefault()
                  return
                }
                trackSettingsTabNavigation(track, SETTINGS_SECTION.calendar, slug, sourceUrl)
              }}
              selected={calendarMemoryActiveSlug === slug}
              data-analytics-id={navigationAnalyticsIdForSettingsTab(SETTINGS_SECTION.calendar, slug)}
            >
              {translateCalendarSubnavLabel(slug)}
            </Tab>
          ))}
        </Tabs>
      )
    }

    if (effectiveSec === SETTINGS_SECTION.ai_notetaker) {
      const notetakerTabs = getSettingsModalNotetakerTabs(notetakerTabCtx)
      if (notetakerTabs.length <= 1) {
        return undefined
      }
      return (
        <Tabs underlined aria-label={pageTitle}>
          {notetakerTabs.map(({ slug }) => (
            <Tab
              key={slug}
              as={RouterLink}
              replace
              to={{
                pathname: location.pathname,
                search: mergeSettingsSearch(SETTINGS_SECTION.ai_notetaker, location.search, slug),
                hash: location.hash ?? '',
              }}
              onClick={(event) => {
                if (!confirmDiscardIfDirty()) {
                  event.preventDefault()
                  return
                }
                trackSettingsTabNavigation(track, SETTINGS_SECTION.ai_notetaker, slug, sourceUrl)
              }}
              selected={notetakerActiveSlug === slug}
              data-analytics-id={navigationAnalyticsIdForSettingsTab(SETTINGS_SECTION.ai_notetaker, slug)}
            >
              {translateNotetakerSubnavLabel(slug)}
            </Tab>
          ))}
        </Tabs>
      )
    }

    return undefined
  })()

  const detailPageHeader = (() => {
    if (effectiveSec === SETTINGS_SECTION.adminPeople) {
      return (
        <SettingsModalPeopleHeader
          onClose={closeModal}
          showCloseInHeader={showCloseInPageHeader}
          userGroupsMemoryHistory={userGroupsMemoryHistory}
        />
      )
    }
    if (effectiveSec === SETTINGS_SECTION.orgDirectory) {
      return (
        <SettingsModalOrgDirectoryHeader
          onClose={closeModal}
          showCloseInHeader={showCloseInPageHeader}
        />
      )
    }
    if (effectiveSec === SETTINGS_SECTION.adminManagedEventTypes) {
      return (
        <SettingsModalManagedEventTypesHeader
          onClose={closeModal}
          showCloseInHeader={showCloseInPageHeader}
        />
      )
    }
    // Desktop and mobile: render title + tabs. Close stays desktop-only — the mobile shell
    // already supplies back/title/close chrome.
    const pageHeaderActions = [
      <SettingsModalHeaderActionsHost key="settings-modal-header-actions" />,
    ]
    if (showCloseInPageHeader) {
      pageHeaderActions.push(
        <IconButton
          key="settings-modal-close"
          type="button"
          title={I18n.t('publisher.action.close')}
          aria-label={I18n.t('publisher.action.close')}
          onClick={closeModal}
          icon={IconX}
        />,
      )
    }
    return (
      <PageHeader
        wide
        title={toSentenceCase(pageTitle)}
        actions={pageHeaderActions}
        tabs={pageHeaderTabs}
      />
    )
  })()

  const sanitizedSettingsTab = sanitizeSettingsTab(effectiveSec, settingsTab, {
    joinAnOrgForAccessNav,
    organizationIntegrationsTabEnabled,
    bookingVerificationEnabled,
    reactiveBlockEnabled,
  })
  // AI column footer host keys off the active tab slug; use the same normalized slug as the
  // subnav / pane so a disabled deep link (e.g. settings_tab=notetaker_access with Access off)
  // still enables the Notetaker save/cancel portal.
  const resolvedSettingsTab = effectiveSec === SETTINGS_SECTION.ai_notetaker
    ? notetakerActiveSlug
    : sanitizedSettingsTab

  const descriptor = resolveSettingsModalPaneDescriptor(effectiveSec, {
    adminEnabledSections,
    settingsTab: resolvedSettingsTab,
    personalSecurityMemoryInitialPath,
    accessRegistryInitialPath,
  })

  let columnTestId = dataTestId
  if (columnTestId == null) {
    if (descriptor.dataTestIdOverride) {
      columnTestId = descriptor.dataTestIdOverride
    } else if (effectiveSec === SETTINGS_SECTION.adminPeople) {
      columnTestId = 'settings-modal-body-admin-people'
    } else if (effectiveSec === SETTINGS_SECTION.orgDirectory) {
      columnTestId = 'settings-modal-body-org-directory'
    } else if (effectiveSec === SETTINGS_SECTION.profile) {
      columnTestId = 'settings-modal-body-profile'
    } else {
      columnTestId = `settings-modal-body-${effectiveSec.replace(/[^a-z0-9]+/g, '-')}`
    }
  }

  const showFooterHost = Boolean(descriptor.showFooterHost)

  const TabbedSectionHeader = MEMORY_ROUTE_TABBED_HEADER_BY_SECTION[effectiveSec]
  if (
    descriptor.kind === SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE
    && TabbedSectionHeader != null
  ) {
    const HeaderComponent = TabbedSectionHeader
    return (
      <SettingsModalMemoryRouteShell
        basePath={descriptor.basePath}
        initialPath={descriptor.initialPath}
        remountKey={descriptor.remountKey}
        browserHistory={history}
        browserLocation={location}
        settingsSection={effectiveSec}
      >
        <SettingsModalContentColumn
          header={(
            <HeaderComponent
              title={pageTitle}
              onClose={closeModal}
              showCloseInHeader={showCloseInPageHeader}
            />
          )}
          showFooterHost={showFooterHost}
          variant={variant}
          dataTestId={columnTestId}
        >
          <Route path={descriptor.basePath} component={descriptor.component} />
        </SettingsModalContentColumn>
      </SettingsModalMemoryRouteShell>
    )
  }

  return (
    <SettingsModalContentColumn
      header={detailPageHeader}
      showFooterHost={showFooterHost}
      variant={variant}
      dataTestId={columnTestId}
    >
      {renderSettingsModalPaneBody(descriptor, {
        browserHistory: history,
        browserLocation: location,
        componentProps: effectiveSec === SETTINGS_SECTION.adminPeople
          ? { userGroupsMemoryHistory }
          : undefined,
        settingsSection: effectiveSec,
      })}
    </SettingsModalContentColumn>
  )
}
